import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { getApiBaseUrl } from '../config/apiBase';
import './VastAdPreview.css';

/** Short sample used as IMA “content” so linear VAST ads can run as preroll/midroll. */
const CONTENT_VIDEO_URL =
  'https://storage.googleapis.com/interactive-media-ads/media/android.mp4';

let imaSdkPromise = null;

function loadImaSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('IMA SDK requires a browser'));
  }
  if (window.google && window.google.ima) {
    return Promise.resolve();
  }
  if (!imaSdkPromise) {
    imaSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-gsradio-ima="1"]');
      if (existing) {
        const done = () => {
          if (window.google && window.google.ima) resolve();
          else reject(new Error('IMA SDK failed to initialize'));
        };
        if (existing.getAttribute('data-loaded') === '1') {
          done();
          return;
        }
        existing.addEventListener('load', done);
        existing.addEventListener('error', () => reject(new Error('IMA script error')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
      s.async = true;
      s.dataset.gsradioIma = '1';
      s.onload = () => {
        s.setAttribute('data-loaded', '1');
        resolve();
      };
      s.onerror = () => reject(new Error('Failed to load IMA SDK'));
      document.body.appendChild(s);
    });
  }
  return imaSdkPromise;
}

/**
 * VAST preview using Google IMA HTML5 SDK.
 * @param {'admin'|'adPreviewFullscreen'} embedMode - admin: manual play + docs; adPreviewFullscreen: fills AdPreview, autoplays when tag exists
 */
function VastAdPreview({ vastTagUrl, embedMode = 'admin' }) {
  const reactId = useId();
  const safeId = reactId.replace(/:/g, '');
  const videoDomId = `vast-preview-video-${safeId}`;

  const videoRef = useRef(null);
  const adContainerRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const vastBlobUrlRef = useRef(null); // legacy fallback when /api/vast-proxy/session is unavailable
  const requestTokenRef = useRef(0);

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const destroyIma = useCallback(() => {
    const video = videoRef.current;
    try {
      if (adsManagerRef.current) {
        adsManagerRef.current.destroy();
        adsManagerRef.current = null;
      }
    } catch (e) {
      console.warn('[VAST preview] adsManager.destroy', e);
    }
    try {
      if (adsLoaderRef.current) {
        adsLoaderRef.current.destroy();
        adsLoaderRef.current = null;
      }
    } catch (e) {
      console.warn('[VAST preview] adsLoader.destroy', e);
    }
    try {
      if (adDisplayContainerRef.current && typeof adDisplayContainerRef.current.destroy === 'function') {
        adDisplayContainerRef.current.destroy();
      }
    } catch (e) {
      console.warn('[VAST preview] adDisplayContainer.destroy', e);
    }
    adDisplayContainerRef.current = null;
    if (vastBlobUrlRef.current) {
      URL.revokeObjectURL(vastBlobUrlRef.current);
      vastBlobUrlRef.current = null;
    }
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  }, []);

  const runPreview = useCallback(async () => {
    const trimmed = (vastTagUrl || '').trim();
    if (!trimmed) {
      setErrorMessage('הזן כתובת VAST Tag לפני תצוגה מקדימה.');
      return;
    }

    let parsed;
    try {
      parsed = new URL(trimmed);
    } catch {
      setErrorMessage('כתובת VAST לא תקינה.');
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      setErrorMessage('יש להשתמש בכתובת http או https.');
      return;
    }

    const token = requestTokenRef.current + 1;
    requestTokenRef.current = token;
    setErrorMessage('');
    setStatus('loading');

    destroyIma();

    try {
      await loadImaSdk();
    } catch (e) {
      if (requestTokenRef.current !== token) return;
      setStatus('error');
      setErrorMessage(e.message || 'טעינת IMA נכשלה');
      return;
    }

    if (requestTokenRef.current !== token) return;

    const apiBase = getApiBaseUrl().replace(/\/+$/, '');

    /** Real HTTPS URL for IMA (avoids error 1005 from blob: adTagUrl). Session stores resolved XML server-side. */
    let adTagUrlForIma;
    try {
      let sessionRes = await fetch(`${apiBase}/api/vast-proxy/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed, unwrap: true }),
        credentials: 'omit',
      });

      if (sessionRes.status === 404 || sessionRes.status === 405) {
        sessionRes = await fetch(`${apiBase}/api/vast-proxy/session/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed, unwrap: true }),
          credentials: 'omit',
        });
      }

      const sessionMissing = sessionRes.status === 404 || sessionRes.status === 405;

      if (sessionRes.ok) {
        const data = await sessionRes.json().catch(() => ({}));
        const sid = data && data.sessionId;
        if (sid && typeof sid === 'string') {
          adTagUrlForIma = `${apiBase}/api/vast-proxy/session/${encodeURIComponent(sid)}`;
        }
      } else if (!sessionMissing) {
        const errBody = await sessionRes.text().catch(() => '');
        let msg = errBody.trim() || `שגיאת שרת (${sessionRes.status})`;
        try {
          const j = JSON.parse(errBody);
          if (j && j.error) msg = String(j.error);
        } catch (_) {
          /* plain text */
        }
        if (requestTokenRef.current !== token) return;
        setStatus('error');
        setErrorMessage(msg);
        return;
      }

      if (!adTagUrlForIma) {
        let res = await fetch(`${apiBase}/api/vast-proxy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed, unwrap: true }),
          credentials: 'omit',
        });
        let xmlText = await res.text();

        if ((res.status === 404 || res.status === 405) && requestTokenRef.current === token) {
          console.warn(
            '[VAST preview] POST /api/vast-proxy missing — using GET. Restart landing-page/server for POST (needed for very long VAST URLs).'
          );
          res = await fetch(
            `${apiBase}/api/vast-proxy?url=${encodeURIComponent(trimmed)}&unwrap=1`,
            { method: 'GET', cache: 'no-store', credentials: 'omit' }
          );
          xmlText = await res.text();
        }

        if (!res.ok) {
          if (requestTokenRef.current !== token) return;
          setStatus('error');
          const hint =
            res.status === 404 && xmlText.includes('Cannot POST')
              ? ' עצור והפעל מחדש את שרת ה-API מתוך landing-page/server (נדרש קוד עדכני עם POST).'
              : '';
          setErrorMessage((xmlText.trim() || `שגיאת שרת VAST (${res.status})`) + hint);
          return;
        }
        if (!xmlText.trim()) {
          if (requestTokenRef.current !== token) return;
          setStatus('error');
          setErrorMessage('תשובת VAST ריקה מהשרת');
          return;
        }
        if (vastBlobUrlRef.current) {
          URL.revokeObjectURL(vastBlobUrlRef.current);
        }
        vastBlobUrlRef.current = URL.createObjectURL(new Blob([xmlText], { type: 'application/xml' }));
        adTagUrlForIma = vastBlobUrlRef.current;
        console.warn(
          '[VAST preview] Using blob adTagUrl fallback — upgrade API for /api/vast-proxy/session to reduce IMA 1005.'
        );
      }
    } catch (e) {
      if (requestTokenRef.current !== token) return;
      console.error('[VAST preview] VAST session/proxy', e);
      setStatus('error');
      setErrorMessage(
        e && e.message && e.message.includes('Failed to fetch')
          ? `לא ניתן להגיע ל-${apiBase}. ודא ששרת ה-API רץ.`
          : e.message || 'שגיאה בטעינת VAST'
      );
      return;
    }

    if (requestTokenRef.current !== token) return;

    const video = videoRef.current;
    const adContainer = adContainerRef.current;
    if (!video || !adContainer) {
      setStatus('error');
      setErrorMessage('אלמנטי נגן לא זמינים.');
      return;
    }

    const google = window.google;
    const ima = google.ima;

    try {
      if (ima.settings) {
        if (typeof ima.settings.setDisableCustomPlaybackForIOS10Plus === 'function') {
          ima.settings.setDisableCustomPlaybackForIOS10Plus(false);
        }
        if (
          typeof ima.settings.setVpaidMode === 'function' &&
          ima.ImaSdkSettings &&
          ima.ImaSdkSettings.VpaidMode
        ) {
          ima.settings.setVpaidMode(ima.ImaSdkSettings.VpaidMode.INSECURE);
        }
      }
    } catch (e) {
      console.warn('[VAST preview] IMA settings', e);
    }

    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.playsInline = true;
    video.muted = embedMode === 'adPreviewFullscreen';

    const rect = video.getBoundingClientRect();
    const w = Math.max(320, Math.floor(rect.width) || 640);
    const h = Math.max(180, Math.floor((w * 9) / 16));

    const onAdError = (ev) => {
      const err = ev.getError ? ev.getError() : ev;
      let msg = err && err.getMessage ? err.getMessage() : String(err || 'שגיאת VAST');
      if (err && typeof err.getErrorCode === 'function') {
        const code = err.getErrorCode();
        if (code != null) msg += ` (קוד ${code})`;
      }
      if (err && typeof err.getInnerError === 'function') {
        const inner = err.getInnerError();
        if (inner) console.error('[VAST preview] Inner error:', inner);
      }
      console.error('[VAST preview] Ad error:', msg);
      setErrorMessage(msg);
      setStatus('error');
      destroyIma();
    };

    const onContentPause = () => {
      setStatus('playing');
    };

    const onContentResume = () => {
      setStatus('ended');
    };

    const onAllAdsCompleted = () => {
      setStatus('ended');
      destroyIma();
    };

    const onAdsManagerLoaded = (e) => {
      try {
        const adsRenderingSettings = new ima.AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
        const adsManager = e.getAdsManager(video, adsRenderingSettings);
        adsManagerRef.current = adsManager;

        adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onAdError);
        adsManager.addEventListener(ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, onContentPause);
        adsManager.addEventListener(ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, onContentResume);
        adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, onAllAdsCompleted);

        adsManager.init(w, h, ima.ViewMode.NORMAL);
        adsManager.start();
      } catch (err) {
        console.error('[VAST preview] adsManager init', err);
        setErrorMessage(err.message || 'הפעלת המודעה נכשלה');
        setStatus('error');
        destroyIma();
      }
    };

    try {
      adDisplayContainerRef.current = new ima.AdDisplayContainer(adContainer, video);
      adDisplayContainerRef.current.initialize();

      adsLoaderRef.current = new ima.AdsLoader(adDisplayContainerRef.current);
      adsLoaderRef.current.addEventListener(
        ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded,
        false
      );
      adsLoaderRef.current.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);

      const adsRequest = new ima.AdsRequest();
      adsRequest.adTagUrl = adTagUrlForIma;
      adsRequest.linearAdSlotWidth = w;
      adsRequest.linearAdSlotHeight = h;
      adsRequest.nonLinearAdSlotWidth = w;
      adsRequest.nonLinearAdSlotHeight = h;
      if ('vastLoadTimeout' in adsRequest) {
        adsRequest.vastLoadTimeout = 30000;
      }

      video.src = CONTENT_VIDEO_URL;

      await new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('זמן טעינת וידאו יסודי פג')), 20000);
        video.onloadedmetadata = () => {
          clearTimeout(t);
          resolve();
        };
        video.onerror = () => {
          clearTimeout(t);
          reject(new Error('לא ניתן לטעון וידאו יסודי לתצוגה מקדימה'));
        };
        video.load();
      });

      if (requestTokenRef.current !== token) return;

      adsLoaderRef.current.requestAds(adsRequest);

      video
        .play()
        .catch((err) => {
          console.warn('[VAST preview] play()', err);
          setErrorMessage('הדפדפן חסם השמעה — לחץ שוב או אשר השמעה.');
          setStatus('error');
        });
    } catch (err) {
      console.error('[VAST preview]', err);
      setErrorMessage(err.message || 'שגיאה לא צפויה');
      setStatus('error');
      destroyIma();
    }
  }, [destroyIma, vastTagUrl, embedMode]);

  const handleStop = useCallback(() => {
    requestTokenRef.current += 1;
    destroyIma();
    setStatus('idle');
    setErrorMessage('');
  }, [destroyIma]);

  useEffect(() => () => {
    requestTokenRef.current += 1;
    destroyIma();
  }, [destroyIma]);

  const isEmbedFullscreen = embedMode === 'adPreviewFullscreen';

  useEffect(() => {
    if (!isEmbedFullscreen) return;
    const trimmed = (vastTagUrl || '').trim();
    if (!trimmed) return;
    let cancelled = false;
    const tid = setTimeout(() => {
      if (!cancelled) runPreview();
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [isEmbedFullscreen, vastTagUrl, runPreview]);

  const busy = status === 'loading';
  const apiBaseLabel = getApiBaseUrl().replace(/\/+$/, '');

  return (
    <div
      className={`vast-ad-preview ${isEmbedFullscreen ? 'vast-ad-preview--embed-fullscreen' : ''}`}
    >
      <div className="vast-ad-preview-stage">
        <video
          id={videoDomId}
          ref={videoRef}
          className="vast-ad-preview-video"
          controls
          playsInline
        />
        <div ref={adContainerRef} className="vast-ad-preview-ad-slot" />
      </div>
      <div className="vast-ad-preview-actions">
        <button
          type="button"
          className="vast-ad-preview-play"
          onClick={runPreview}
          disabled={busy}
        >
          {busy ? 'טוען…' : isEmbedFullscreen ? 'נגן שוב' : 'הפעל תצוגה מקדימה'}
        </button>
        <button type="button" className="vast-ad-preview-stop" onClick={handleStop}>
          עצור
        </button>
      </div>
      {status === 'playing' && (
        <p className="vast-ad-preview-hint">מנגן מודעת VAST…</p>
      )}
      {status === 'ended' && !errorMessage && (
        <p className="vast-ad-preview-hint">המודעה הסתיימה (או אין מודעה בתג).</p>
      )}
      {errorMessage && (
        <p className="vast-ad-preview-error" role="alert">
          {errorMessage}
        </p>
      )}
      {!isEmbedFullscreen && (
        <p className="vast-ad-preview-footnote">
          ה-IMA נטען עם כתובת HTTPS קצרה: <code>POST {apiBaseLabel}/api/vast-proxy/session</code> יוצר session, ואז{' '}
          <code>GET …/api/vast-proxy/session/&lt;id&gt;</code> מחזיר XML (wrappers עד 15 בשרת). אם endpoint ה-session
          חסר, יבוצע נסיגה ל-blob. תגים ארוכים: <code>POST {apiBaseLabel}/api/vast-proxy</code> עם גוף JSON.
        </p>
      )}
    </div>
  );
}

export default VastAdPreview;
